import type {
  FlowTriggerNode,
  FlowFilterNode,
  FlowTriggerNodeConfig,
  FlowFilterNodeConfig,
  FlowAgentNode,
  FlowAgentTaskNode,
  FlowApprovalNode,
  FlowRouterNode,
  FlowLLMNode,
  FlowActionNode,
  FlowAgentNodeConfig,
  FlowAgentTaskNodeConfig,
  FlowApprovalNodeConfig,
  FlowRouterNodeConfig,
  FlowLLMNodeConfig,
  FlowActionNodeConfig,
  FlowApprovalAssignee,
  FlowEdge,
  FlowPosition,
  FlowJoinType,
  FlowDefinition,
  FlowRun,
} from './types/index.js';

interface NodeOptions {
  joinType?: FlowJoinType;
  maxIterations?: number;
  position?: FlowPosition;
}

const TERMINAL_FLOW_EVENTS = new Set(['flow.completed', 'flow.error', 'flow.cancelled']);

export function isTerminalFlowEvent(type: string): boolean {
  return TERMINAL_FLOW_EVENTS.has(type);
}

export function triggerNode(
  id: string,
  name: string,
  config: FlowTriggerNodeConfig,
  opts?: NodeOptions,
): FlowTriggerNode {
  return {
    type: 'trigger',
    id,
    name,
    config,
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

export function filterNode(
  id: string,
  name: string,
  config: FlowFilterNodeConfig,
  opts?: NodeOptions,
): FlowFilterNode {
  return {
    type: 'filter',
    id,
    name,
    config,
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

export function agentNode(
  id: string,
  name: string,
  config: FlowAgentNodeConfig,
  opts?: NodeOptions,
): FlowAgentNode {
  return {
    type: 'agent',
    id,
    name,
    config,
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

export function agentTaskNode(
  id: string,
  name: string,
  config: Omit<FlowAgentTaskNodeConfig, 'responseFormat' | 'outputKey'> & {
    responseFormat?: FlowAgentTaskNodeConfig['responseFormat'];
    outputKey?: string;
  },
  opts?: NodeOptions,
): FlowAgentTaskNode {
  return {
    type: 'agent_task',
    id,
    name,
    config: { responseFormat: 'text', outputKey: 'result', ...config },
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

export function approvalNode(
  id: string,
  name: string,
  config: Omit<FlowApprovalNodeConfig, 'allowFeedback'> & { allowFeedback?: boolean },
  opts?: NodeOptions,
): FlowApprovalNode {
  return {
    type: 'approval',
    id,
    name,
    config: { allowFeedback: true, ...config },
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

export function routerNode(
  id: string,
  name: string,
  config: FlowRouterNodeConfig,
  opts?: NodeOptions,
): FlowRouterNode {
  return {
    type: 'router',
    id,
    name,
    config,
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

export function llmNode(
  id: string,
  name: string,
  config: Omit<FlowLLMNodeConfig, 'responseFormat' | 'outputKey'> & {
    responseFormat?: FlowLLMNodeConfig['responseFormat'];
    outputKey?: string;
  },
  opts?: NodeOptions,
): FlowLLMNode {
  return {
    type: 'llm',
    id,
    name,
    config: { responseFormat: 'text', outputKey: 'llmResult', ...config },
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

export function actionNode(
  id: string,
  name: string,
  config: FlowActionNodeConfig,
  opts?: NodeOptions,
): FlowActionNode {
  return {
    type: 'action',
    id,
    name,
    config,
    joinType: opts?.joinType ?? 'and',
    maxIterations: opts?.maxIterations ?? 5,
    position: opts?.position ?? { x: 0, y: 0 },
  };
}

/**
 * Resolve the effective approval targets for an approval node. The gate
 * exists for a human to review the run's work, so resolution prefers the
 * human closest to the run:
 *   1. the node's explicit `reportTo`, when non-empty;
 *   2. the run's owner (`run.userId`) as a user assignee. A machine
 *      identity (`svc-*`/`sva_` service accounts, the system user — any
 *      non-`usr_` id) cannot log in, so a gate assigned to one is
 *      unanswerable and widens to the whole org instead;
 *   3. for ownerless runs, the owning flow's owner, derived from the
 *      `ownerId` prefix: `usr_` -> that user, `tem_` -> that team,
 *      anything else (org sentinel, null, unknown) -> the whole org.
 */
export function resolveApprovalAssignees(
  node: FlowApprovalNode,
  run: Pick<FlowRun, 'userId'>,
  flow: Pick<FlowDefinition, 'ownerId'>,
): FlowApprovalAssignee[] {
  const explicit = node.config.reportTo;
  if (explicit && explicit.length > 0) return explicit;

  const runUserId = run.userId ?? null;
  if (runUserId) {
    if (runUserId.startsWith('usr_')) {
      return [{ kind: 'user', userId: runUserId }];
    }
    return [{ kind: 'organization' }];
  }

  const ownerId = flow.ownerId ?? null;
  if (ownerId && ownerId.startsWith('usr_')) {
    return [{ kind: 'user', userId: ownerId }];
  }
  if (ownerId && ownerId.startsWith('tem_')) {
    return [{ kind: 'team', teamId: ownerId }];
  }
  return [{ kind: 'organization' }];
}

export function edge(
  id: string,
  source: string,
  target: string,
  condition?: string,
  label?: string,
): FlowEdge {
  return {
    id,
    source,
    target,
    ...(condition !== undefined ? { condition } : {}),
    ...(label !== undefined ? { label } : {}),
  };
}

/**
 * Create a conditional edge (shorthand for edge with condition).
 */
export function conditionalEdge(
  id: string,
  source: string,
  target: string,
  condition: string,
  label?: string,
): FlowEdge {
  return edge(id, source, target, condition, label ?? condition);
}
